import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch } from 'react-redux';
import { ImagemapTemplateType } from '../../app/types';
import { color } from '../../app/theme';
import TextTemplate from './templates/TextTemplate';
import FileTemplate from './templates/FileTemplate';
import RichVideoTemplate from './templates/RichVideoTemplate';
import OptionalCardTemplate from './templates/OptionalCardTemplate';
import IconAddCircle from '../../assets/images/Icons/IconAddCircle';
import ConfirmCardTemplate from './templates/ConfirmCardTemplate';
import ImageCardTemplate from './templates/ImageCardTemplate';
import ImagemapTemplate from './templates/ImagemapTemplate';
import { randomText, validateMessages } from '../../app/utils';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 44,
    },
    inputTitle: {
      fontSize: 15,
      fontWeight: 500,
      color: color.$3,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    message: {
      width: 606,
    },
    'message-small': {
      width: '100%',
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    errorMessage: {
      color: color.$17,
    },
  },
  'Messages',
);

const Messages = ({
  title,
  disabled,
  addMessage,
  deleteMessage,
  upMessage,
  downMessage,
  isSaved,
  messages,
  onChange = () => {},
  platform,
  dataKey = 'success',
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const platformType = platform;
  const isLessVW1280 = useMediaQuery('(max-width:1280px)');
  const messageLength = messages.length;

  function handleChangeMessage(data, dataIndex) {
    onChange({
      messages: messages.map((msg, index) => {
        if (index === dataIndex) {
          return {
            ...data,
            index,
          };
        }
        return msg;
      }),
    });
  }

  function onChangeType(type, index) {
    onChange({
      messages: messages.map((msg, messageIndex) => {
        if (index === messageIndex) {
          switch (type) {
            case 'text':
              return {
                contentType: 'text/plain',
                data: {
                  content: '',
                },
                index,
              };
            case 'file':
              return {
                contentType: 'application/x-image',
                data: {
                  name: '',
                  contentType: '',
                  url: '',
                  previewUrl: null,
                },
                index,
              };
            case 'richvideo':
              return {
                className: 'Template',
                templateType: 'richvideo',
                template: randomText(),
                data: {
                  elements: [
                    {
                      fileName: '',
                      videoUrl: '',
                      previewImageUrl: '',
                      aspectRatio: '20:13',
                      buttons: [
                        {
                          type: 'url',
                          title: '',
                          data: '',
                          tags: [],
                        },
                      ],
                    },
                  ],
                },
                index,
              };
            case 'card':
              return {
                contentType: 'application/x-template',
                template: randomText(),
                data: {
                  templateType: 'card',
                  elements: [
                    {
                      goodsURL: '',
                      imageType: 'upload',
                      imageUrl: '',
                      title: '',
                      subtitle: '',
                      buttons: [
                        {
                          type: 'postback',
                          title: '',
                          data: '',
                          tags: [],
                        },
                      ],
                    },
                  ],
                },
                index,
              };
            case 'confirm':
              return {
                contentType: 'application/x-template',
                template: randomText(),
                data: {
                  templateType: 'confirm',
                  elements: [
                    {
                      title: '',
                      buttons: [
                        {
                          type: 'postback',
                          title: '',
                          data: '',
                          tags: [],
                        },
                        {
                          type: 'postback',
                          title: '',
                          data: '',
                          tags: [],
                        },
                      ],
                    },
                  ],
                },
                index,
              };
            case 'image':
              return {
                contentType: 'application/x-template',
                template: randomText(),
                data: {
                  templateType: 'image',
                  elements: [
                    {
                      goodsURL: '',
                      imageType: 'upload',
                      imageUrl: '',
                      buttons: [
                        {
                          type: 'postback',
                          title: '',
                          data: '',
                          tags: [],
                        },
                      ],
                    },
                  ],
                },
                index,
              };
            case 'imagemap':
              return {
                contentType: 'application/x-template',
                template: randomText(),
                data: {
                  templateType: 'imagemap',
                  elements: [
                    {
                      name: '',
                      imageUrl: '',
                      size: {
                        width: 1040,
                        height: 1040,
                      },
                      aspectRatio: '1:1',
                      messageTemplateType: 'ImagemapTemplate1',
                      buttons: ImagemapTemplateType.ImagemapTemplate1,
                    },
                  ],
                },
                index,
              };
            default:
              return null;
          }
        }
        return msg;
      }),
    });
  }

  function handleAddMessage() {
    dispatch(addMessage(dataKey));
  }

  function handleDeleteMessage(index) {
    dispatch(deleteMessage(index, dataKey));
  }

  function handleUpMessage(index) {
    dispatch(upMessage(index, dataKey));
  }

  function handleDownMessage(index) {
    dispatch(downMessage(index, dataKey));
  }

  return (
    <div className={classes.root}>
      <div className={classes.inputTitle}>{title || t('message')}</div>
      <div
        className={classnames(classes.message, {
          [classes['message-small']]: isLessVW1280,
        })}
      >
        {messages.map((temp, index) => {
          switch (temp.contentType) {
            case 'text/plain':
              return (
                <TextTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  disabled={disabled}
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            case 'application/x-image':
            case 'application/x-audio':
            case 'application/x-video':
              return (
                <FileTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  disabled={disabled}
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            default:
          }
          switch (temp.templateType || temp.data.templateType) {
            case 'richvideo':
              return (
                <RichVideoTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  disabled={disabled}
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            case 'card':
              return (
                <OptionalCardTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  messageIndex={temp.index}
                  disabled={disabled}
                  isBroadcast
                  disableTemplate
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            case 'confirm':
              return (
                <ConfirmCardTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  messageIndex={temp.index}
                  disabled={disabled}
                  isBroadcast
                  disableTemplate
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            case 'image':
              return (
                <ImageCardTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  messageIndex={temp.index}
                  disabled={disabled}
                  isBroadcast
                  disableTemplate
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            case 'imagemap':
              return (
                <ImagemapTemplate
                  key={`template-${temp.index}`}
                  template={temp}
                  onChangeType={type => onChangeType(type, index)}
                  onChange={data => handleChangeMessage(data, index)}
                  onDelete={handleDeleteMessage}
                  platformType={platformType}
                  onUp={handleUpMessage}
                  onDown={handleDownMessage}
                  messageLength={messageLength}
                  disabled={disabled}
                  isBroadcast
                  isError={isSaved && !validateMessages([temp])}
                />
              );
            default:
          }
          return null;
        })}
      </div>
      {!disabled && messages.length < 3 && (
        <div className={classes.icon} onClick={handleAddMessage}>
          <IconAddCircle id="icon-add-message-type" width={20} height={20} />
          {t('addMessages')}
        </div>
      )}
    </div>
  );
};

export default Messages;
